import { Injectable, inject, signal, effect, DestroyRef } from '@angular/core';

import { ViewerCoreService } from './viewer-core.service.js';
import { SceneStateService } from './scene-state.service';

export interface ClassificationEntry {
  code: number;
  name: string;
  color: [number, number, number, number];
  visible: boolean;
}

@Injectable({ providedIn: 'root' })
export class PointcloudService {
  private core = inject(ViewerCoreService);
  private sceneState = inject(SceneStateService);
  private destroyRef = inject(DestroyRef);

  // ─── Reactive state ─────────────────────────────────────────────────────────

  readonly pointCloudVisible = signal(true);
  readonly classifications = signal<ClassificationEntry[]>([]);

  private _syncing = false;

  constructor() {
    effect(() => {
      const viewer = this.core.viewer();
      if (!viewer) return;
      this._syncFromViewer(viewer);
    });

    // Re-sync when pointclouds change
    effect(() => {
      const pointclouds = this.sceneState.pointclouds();
      if (!pointclouds.length) return;
      const viewer = this.core.viewer();
      if (viewer) this._syncFromViewer(viewer);
    });

    effect(() => {
      const visible = this.pointCloudVisible();
      if (this._syncing) return;
      const pointclouds = this.sceneState.pointclouds();
      for (const pc of pointclouds) {
        pc.visible = visible;
      }
    });
  }

  // ─── Actions ──────────────────────────────────────────────────────────────

  setClassificationVisibility(code: number, visible: boolean): void {
    const viewer = this.core.viewer();
    if (!viewer?.classifications) return;
    const entry = viewer.classifications[code];
    if (entry) {
      entry.visible = visible;
      this._refreshClassifications(viewer);
    }
  }

  toggleAllClassifications(visible?: boolean): void {
    const viewer = this.core.viewer();
    if (!viewer?.classifications) return;
    const entries = Object.values(viewer.classifications) as any[];
    const target = visible === undefined ? !entries.every(e => e.visible !== false) : visible;
    entries.forEach(e => (e.visible = target));
    this._refreshClassifications(viewer);
  }

  setClassificationColor(code: number, rgb: [number, number, number]): void {
    const viewer = this.core.viewer();
    if (!viewer?.classifications) return;
    const entry = viewer.classifications[code];
    if (entry) {
      entry.color = [rgb[0] / 255, rgb[1] / 255, rgb[2] / 255, 1];
      this._refreshClassifications(viewer);
    }
  }

  // ─── Private ──────────────────────────────────────────────────────────────

  private _syncFromViewer(viewer: any): void {
    this._syncing = true;
    const pointclouds = this.sceneState.pointclouds();
    if (pointclouds.length > 0) {
      this.pointCloudVisible.set(pointclouds[0].visible !== false);
    }
    this._refreshClassifications(viewer);
    this._syncing = false;
  }

  private _refreshClassifications(viewer: any): void {
    if (!viewer?.classifications) {
      this.classifications.set([]);
      return;
    }
    this.classifications.set(
      Object.entries(viewer.classifications).map(([code, entry]: any) => ({
        code: Number(code),
        name: entry.name ?? `Class ${code}`,
        color: entry.color ?? [1, 1, 1, 1],
        visible: entry.visible !== false,
      })),
    );
  }
}
